/**
 * useCapabilities — the client-side capability check the CatalogPicker uses to
 * decide whether to render a catalog's admin ↗ link (§22c, belt-and-suspenders
 * to the server's omission of the `admin` block).
 *
 * Capabilities derive from the user's role in the prefs slice. In a real
 * deployment these come from the auth token's claims (see the RCI auth
 * standard); here `data_admin` is treated as holding the catalog-manage
 * capabilities. `field` users hold none — and, crucially, the server never
 * sends them the `admin` URL in the first place, so there is nothing to leak.
 */
import { useCallback } from "react";
import { useAppSelector } from "@app/hooks";
import { catalogCapabilities, capsSatisfy } from "@api/capabilities";

export function useCapabilities() {
  const role = useAppSelector((s) => s.prefs.role);

  /** Does the current user hold `capability`? Uses the same caps model the
   * server gates on (mApi forwards these via X-Oxbo-Caps), so client + server
   * agree — this check is belt-and-suspenders, not the real gate (§22c). */
  const can = useCallback(
    (capability: string | undefined): boolean => {
      if (!capability) return false;
      return capsSatisfy(catalogCapabilities(role), capability);
    },
    [role],
  );

  return { role, can };
}
